/**
 * AdvanceConfigs — Configuration des acomptes récurrents par salarié.
 * Un salarié = une config (montant + jour + actif + validateur).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users2, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function AdvanceConfigs() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [configs, setConfigs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", amount: "", day_of_month: 15, is_active: true,
    validator_email: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get("/hr/advance-configs"), api.get("/hr/employees"),
      ]);
      setConfigs(c.data.configs || []);
      setEmployees(e.data.employees || []);
    } catch (er) { toast.error(apiError(er)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.employee_id || !form.amount) { toast.error("Salarié et montant requis"); return; }
    try {
      await api.post(`/hr/employees/${form.employee_id}/advance-config`, {
        amount: parseFloat(form.amount),
        day_of_month: parseInt(form.day_of_month),
        is_active: form.is_active,
        validator_email: form.validator_email || null,
        notes: form.notes || null,
      });
      toast.success("Configuration enregistrée");
      setOpen(false);
      setForm({ employee_id: "", amount: "", day_of_month: 15, is_active: true, validator_email: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/hr/advance-configs/${c.id}`, { is_active: !c.is_active });
      load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette configuration ? Les acomptes déjà générés restent.")) return;
    try { await api.delete(`/hr/advance-configs/${id}`); toast.success("Config supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const empsWithoutConfig = employees.filter(e =>
    !configs.some(c => c.employee_id === e.id) && e.status !== "terminated"
  );

  return (
    <div className="space-y-6" data-testid="advance-configs-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Configurations acomptes récurrents
          </h1>
          <p className="text-sm text-slate-500">
            {configs.length} salarié{configs.length > 1 ? "s" : ""} configuré{configs.length > 1 ? "s" : ""} ·
            {" "}{configs.filter(c => c.is_active).length} actif{configs.filter(c => c.is_active).length > 1 ? "s" : ""}
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="new-config-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle config
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : configs.length === 0 ? (
            <div className="p-12 text-center" data-testid="configs-empty">
              <Users2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucune configuration.</p>
              <p className="text-xs text-slate-400 mt-1">Créez une config pour générer automatiquement les acomptes chaque mois.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">Salarié</TableHead>
                <TableHead className="text-xs uppercase text-right">Montant</TableHead>
                <TableHead className="text-xs uppercase">Jour du virement</TableHead>
                <TableHead className="text-xs uppercase">Validateur email</TableHead>
                <TableHead className="text-xs uppercase">Actif</TableHead>
                <TableHead className="text-xs uppercase text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {configs.map((c, i) => (
                  <TableRow key={c.id} className="border-b" data-testid={`config-row-${i}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{c.employee_name}</div>
                      <div className="text-xs text-slate-500">{c.job_title || "—"}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {Number(c.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                    </TableCell>
                    <TableCell>Le <b>{c.day_of_month}</b> du mois</TableCell>
                    <TableCell className="text-sm font-mono text-slate-600">
                      {c.validator_email || <span className="text-slate-400 italic">Email RH global</span>}
                    </TableCell>
                    <TableCell>
                      <Switch checked={c.is_active} onCheckedChange={() => toggle(c)}
                        disabled={!canWrite} data-testid={`toggle-${c.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <Button size="sm" variant="ghost" className="text-red-600 h-7"
                          onClick={() => remove(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-config-dialog">
          <DialogHeader><DialogTitle>Nouvelle configuration d'acompte récurrent</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Salarié *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger data-testid="config-emp-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{empsWithoutConfig.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Montant mensuel (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.amount}
                  onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                  data-testid="config-amount" />
              </div>
              <div><Label>Jour du virement (1-28)</Label>
                <Input type="number" min="1" max="28" value={form.day_of_month}
                  onChange={(e) => setForm(p => ({ ...p, day_of_month: e.target.value }))} />
              </div>
            </div>
            <div><Label>Validateur (email pour magic-link, laisser vide = email RH global)</Label>
              <Input type="email" value={form.validator_email}
                onChange={(e) => setForm(p => ({ ...p, validator_email: e.target.value }))}
                placeholder="manager@..." />
            </div>
            <div><Label>Notes internes</Label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between pt-3 border-t">
              <Label>Actif (génère les acomptes chaque mois)</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-config-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
